import React, { useEffect, useState, useRef } from "react";
import ICalSyncManager from "../../components/ICalSyncManager";
import api, { fileUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { formatMoney } from "../../lib/auth";
import { Plus, Copy, Trash2, ImagePlus, Star, ExternalLink, RefreshCcw, Pencil, GripVertical, Film, Upload as UploadIcon, X as XIcon } from "lucide-react";
import HouseImagePlaceholder from "../../components/HouseImagePlaceholder";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const empty = {
  slug: "", name: "", tagline: "", description: "", city: "", state: "SP",
  address: "", max_guests: 4, bedrooms: 2, beds: 2, bathrooms: 2,
  base_price: 1500, cleaning_fee: 300, min_nights: 2,
  house_rules: "", check_in_time: "15:00", check_out_time: "11:00",
  published: true, amenities: [],
};

export default function AdminHouses() {
  const [houses, setHouses] = useState([]);
  const [editing, setEditing] = useState(null);

  async function load() {
    const r = await api.get("/admin/houses");
    setHouses(r.data);
  }
  useEffect(() => { load(); }, []);

  function newHouse() {
    setEditing({ ...empty, _new: true });
  }

  return (
    <div data-testid="admin-houses-page">
      <div className="mb-8 flex items-end justify-between">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Cadastro</span>
          <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Casas</h1>
        </div>
        <Button onClick={newHouse} className="bg-[#0AA89C] hover:bg-[#066B63]" data-testid="new-house-btn">
          <Plus className="w-4 h-4 mr-1" /> Nova casa
        </Button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {houses.map((h) => (
          <div key={h.id} className="bg-white border border-slate-200 rounded-md overflow-hidden">
            {h.photos?.length > 0 ? (
              <div className="aspect-[4/3] bg-slate-100">
                <img src={fileUrl((h.photos.find(p => p.is_cover) || h.photos[0]).storage_path)} className="w-full h-full object-cover" alt="" />
              </div>
            ) : (
              <HouseImagePlaceholder />
            )}
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-semibold">{h.name}</div>
                  <div className="text-xs text-slate-500">{h.city} / {h.state} · {h.max_guests} hóspedes</div>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded border ${h.published ? "text-emerald-700 border-emerald-200 bg-emerald-50" : "text-slate-600 border-slate-200 bg-slate-50"}`}>
                  {h.published ? "Publicada" : "Rascunho"}
                </span>
              </div>
              <div className="text-sm mt-3">{formatMoney(h.base_price)} <span className="text-xs text-slate-500">/ noite</span></div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing({ ...h })} data-testid={`edit-house-${h.slug}`}>
                  Editar
                </Button>
                <a href={`/casas/${h.slug}`} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-[#0AA89C] self-center inline-flex items-center gap-1">
                  Ver no site <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      <HouseForm key={editing?.id || (editing ? "new-house" : "closed")} house={editing} onClose={() => { setEditing(null); load(); }} />
    </div>
  );
}

function HouseForm({ house, onClose }) {
  const [f, setF] = useState(house);
  const [saving, setSaving] = useState(false);
  const [amenityInput, setAmenityInput] = useState("");
  const [photos, setPhotos] = useState([]);
  const [captionEditing, setCaptionEditing] = useState(null);
  const fileRef = useRef();
  // Onda A · Etapa A2 — vídeo de apresentação
  const [introVideo, setIntroVideo] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoError, setVideoError] = useState(null);
  const videoFileRef = useRef();

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    setF(house);
    setPhotos(house?.photos || []);
    setIntroVideo(house?.intro_video || null);
    setVideoError(null);
    setVideoProgress(0);
  }, [house]);

  if (!house || !f) return null;

  async function submit() {
    setSaving(true);
    try {
      if (f._new) {
        const { _new, photos: _p, ...body } = f;
        await api.post("/admin/houses", body);
        toast.success("Casa criada");
      } else {
        const { id, photos: _p, ...body } = f;
        await api.patch(`/admin/houses/${house.id}`, body);
        toast.success("Casa atualizada");
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  }

  function addAmenity() {
    if (!amenityInput.trim()) return;
    setF({ ...f, amenities: [...(f.amenities || []), { label: amenityInput.trim() }] });
    setAmenityInput("");
  }

  function removeAmenity(i) {
    setF({ ...f, amenities: f.amenities.filter((_, idx) => idx !== i) });
  }

  async function uploadPhoto(file) {
    if (!house.id || f._new) {
      toast.info("Salve a casa antes de adicionar fotos");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/admin/houses/${house.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos([...photos, r.data]);
      toast.success("Foto adicionada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    }
  }

  async function setCover(photoId) {
    await api.patch(`/admin/houses/${house.id}/photos/${photoId}`, { is_cover: true });
    setPhotos(photos.map((p) => ({ ...p, is_cover: p.id === photoId })));
  }

  async function deletePhoto(photoId) {
    if (!confirm("Remover esta foto?")) return;
    await api.delete(`/admin/houses/${house.id}/photos/${photoId}`);
    setPhotos(photos.filter((p) => p.id !== photoId));
  }

  async function replacePhotoFile(photoId, file) {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.put(`/admin/houses/${house.id}/photos/${photoId}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos(photos.map((p) => (p.id === photoId ? r.data : p)));
      toast.success("Arquivo substituído");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao substituir arquivo");
    }
  }

  async function persistOrder(nextOrder) {
    // Optimistic: state already updated by dnd. Persist backend order.
    try {
      await api.patch(`/admin/houses/${house.id}/photos/reorder`, { order: nextOrder.map((p) => p.id) });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não foi possível salvar a nova ordem");
      // Fallback: reload authoritative order from server
      const fresh = await api.get(`/admin/houses/${house.id}`);
      setPhotos(fresh.data.photos || []);
    }
  }

  async function saveCaption() {
    if (!captionEditing) return;
    try {
      await api.patch(`/admin/houses/${house.id}/photos/${captionEditing.id}`, {
        caption: captionEditing.caption || "",
        alt_text: captionEditing.alt_text || "",
      });
      setPhotos(photos.map((p) => (p.id === captionEditing.id
        ? { ...p, caption: captionEditing.caption || null, alt_text: captionEditing.alt_text || null }
        : p)));
      setCaptionEditing(null);
      toast.success("Legenda atualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar legenda");
    }
  }

  // ---------------- Vídeo de apresentação (Onda A · A2) ----------------

  const VIDEO_MAX_MB = 50;
  const VIDEO_ACCEPT_MIMES = ["video/mp4", "video/webm", "video/quicktime"];
  const VIDEO_ACCEPT_ATTR = "video/mp4,video/webm,video/quicktime";

  async function uploadVideo(file) {
    if (!house?.id || f._new) {
      toast.info("Salve a casa antes de enviar o vídeo");
      return;
    }
    // Validação client-side (advisory, o backend revalida)
    if (!VIDEO_ACCEPT_MIMES.includes(file.type)) {
      const msg = "Formato não permitido. Envie MP4, WebM ou MOV.";
      setVideoError(msg);
      toast.error(msg);
      return;
    }
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > VIDEO_MAX_MB) {
      const msg = `Arquivo maior que ${VIDEO_MAX_MB} MB (recebido: ${sizeMB.toFixed(1)} MB).`;
      setVideoError(msg);
      toast.error(msg);
      return;
    }
    setVideoUploading(true);
    setVideoError(null);
    setVideoProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.put(`/admin/houses/${house.id}/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setVideoProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setIntroVideo(r.data);
      toast.success(introVideo ? "Vídeo substituído" : "Vídeo enviado");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Falha ao enviar o vídeo";
      setVideoError(msg);
      toast.error(msg);
    } finally {
      setVideoUploading(false);
      setVideoProgress(0);
      if (videoFileRef.current) videoFileRef.current.value = "";
    }
  }

  async function removeVideo() {
    if (!introVideo) return;
    if (!confirm("Remover o vídeo de apresentação deste imóvel?")) return;
    try {
      await api.delete(`/admin/houses/${house.id}/video`);
      setIntroVideo(null);
      setVideoError(null);
      toast.success("Vídeo removido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover o vídeo");
    }
  }

  async function setVideoPoster(posterPath) {
    if (!introVideo) return;
    try {
      const r = await api.patch(`/admin/houses/${house.id}/video`, {
        poster_path: posterPath,
      });
      setIntroVideo(r.data);
      toast.success(posterPath ? "Capa definida" : "Capa removida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao atualizar a capa");
    }
  }

  function onDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = photos.findIndex((p) => p.id === active.id);
    const newIndex = photos.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = arrayMove(photos, oldIndex, newIndex);
    setPhotos(next);
    persistOrder(next);
  }

  const icalUrl = house.slug ? `${process.env.REACT_APP_BACKEND_URL}/api/ical/houses/${house.slug}.ics` : "";
  const isExistingHouse = house.id && !f._new;

  return (
    <Dialog open={!!house} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{f._new ? "Nova casa" : `Editar: ${f.name}`}</DialogTitle>
          <DialogDescription>
            Cadastro completo do imóvel, incluindo fotos, comodidades, regras de uso e configurações de exibição no site.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label>Nome</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="house-name" />
          </div>
          <div>
            <Label>Slug (URL)</Label>
            <Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="house-slug" />
          </div>
          <div>
            <Label>Cidade</Label>
            <Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
          </div>
          <div>
            <Label>Estado (UF)</Label>
            <Input value={f.state} maxLength={2} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <Label>Endereço completo (visível somente para hóspedes)</Label>
            <Input value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Tagline</Label>
            <Input value={f.tagline || ""} onChange={(e) => setF({ ...f, tagline: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Descrição</Label>
            <Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </div>
          <NumField label="Hóspedes" value={f.max_guests} onChange={(v) => setF({ ...f, max_guests: v })} />
          <NumField label="Quartos" value={f.bedrooms} onChange={(v) => setF({ ...f, bedrooms: v })} />
          <NumField label="Camas" value={f.beds} onChange={(v) => setF({ ...f, beds: v })} />
          <NumField label="Banheiros" value={f.bathrooms} onChange={(v) => setF({ ...f, bathrooms: v })} />
          <NumField label="Preço/noite (R$)" value={f.base_price} onChange={(v) => setF({ ...f, base_price: v })} step={50} />
          <NumField label="Taxa limpeza (R$)" value={f.cleaning_fee} onChange={(v) => setF({ ...f, cleaning_fee: v })} step={10} />
          <NumField label="Mín. noites" value={f.min_nights} onChange={(v) => setF({ ...f, min_nights: v })} />
          <div>
            <Label>Check-in</Label>
            <Input value={f.check_in_time} onChange={(e) => setF({ ...f, check_in_time: e.target.value })} />
          </div>
          <div>
            <Label>Check-out</Label>
            <Input value={f.check_out_time} onChange={(e) => setF({ ...f, check_out_time: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Regras da casa</Label>
            <Textarea rows={3} value={f.house_rules || ""} onChange={(e) => setF({ ...f, house_rules: e.target.value })} />
          </div>

          <div className="col-span-2">
            <Label>Comodidades</Label>
            <div className="flex gap-2 mt-2">
              <Input value={amenityInput} onChange={(e) => setAmenityInput(e.target.value)} placeholder="Ex: Piscina" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAmenity())} />
              <Button type="button" variant="outline" onClick={addAmenity}>Adicionar</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {(f.amenities || []).map((a, i) => (
                <span key={i} className="text-xs border border-slate-200 rounded px-2 py-1 bg-slate-50 flex items-center gap-2">
                  {a.label}
                  <button type="button" onClick={() => removeAmenity(i)} className="text-slate-400 hover:text-red-500">×</button>
                </span>
              ))}
            </div>
          </div>

          <div className="col-span-2 flex items-center gap-3">
            <Switch checked={f.published} onCheckedChange={(v) => setF({ ...f, published: v })} data-testid="house-published" />
            <Label className="!m-0">Publicada no site</Label>
          </div>
        </div>

        {!f._new && (
          <div className="mt-6 border-t border-slate-200 pt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold">Fotos ({photos.length})</div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="upload-photo-btn">
                <ImagePlus className="w-4 h-4 mr-1" /> Adicionar foto
              </Button>
            </div>
            {photos.length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded p-8 text-center text-sm text-slate-500">
                Nenhuma foto ainda — a casa exibirá um placeholder no site.
              </div>
            ) : (
              <>
                <p className="text-xs text-slate-500 mb-3">Arraste as fotos pelo ícone <GripVertical className="inline w-3 h-3" /> para reordenar. A capa é a foto principal exibida no site.</p>
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                  <SortableContext items={photos.map((p) => p.id)} strategy={rectSortingStrategy}>
                    <div className="grid grid-cols-4 gap-3">
                      {photos.map((p) => (
                        <SortablePhoto
                          key={p.id}
                          photo={p}
                          houseId={house.id}
                          onSetCover={() => setCover(p.id)}
                          onDelete={() => deletePhoto(p.id)}
                          onReplaceFile={(file) => replacePhotoFile(p.id, file)}
                          onEditCaption={() => setCaptionEditing({ ...p, caption: p.caption || "", alt_text: p.alt_text || "" })}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </>
            )}

            {/* --- Vídeo de apresentação (Onda A · A2) --- */}
            <div className="mt-6 border-t border-slate-200 pt-4" data-testid="video-section">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Film className="w-4 h-4 text-slate-500" />
                  <div className="text-sm font-semibold">Vídeo de apresentação</div>
                  {introVideo && (
                    <span className="text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100" data-testid="video-status-badge">
                      Disponível
                    </span>
                  )}
                  {!introVideo && (
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-50 text-slate-500 border border-slate-200" data-testid="video-status-badge">
                      Sem vídeo
                    </span>
                  )}
                </div>
                <input
                  ref={videoFileRef}
                  type="file"
                  accept={VIDEO_ACCEPT_ATTR}
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])}
                  data-testid="video-file-input"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => videoFileRef.current?.click()}
                    disabled={videoUploading}
                    data-testid="video-upload-btn"
                  >
                    <UploadIcon className="w-4 h-4 mr-1" />
                    {introVideo ? "Substituir vídeo" : "Enviar vídeo"}
                  </Button>
                  {introVideo && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={removeVideo}
                      disabled={videoUploading}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      data-testid="video-remove-btn"
                    >
                      <XIcon className="w-4 h-4 mr-1" /> Remover
                    </Button>
                  )}
                </div>
              </div>

              <p className="text-xs text-slate-500 mb-3">
                Formatos aceitos: MP4, WebM, MOV. Duração recomendada: 20–40&nbsp;s (máximo 50&nbsp;s).
                Tamanho máximo: {VIDEO_MAX_MB}&nbsp;MB. Grave sem áudio ou com áudio institucional — a reprodução no site será silenciosa.
              </p>

              {videoUploading && (
                <div className="mb-3" data-testid="video-progress">
                  <div className="h-2 w-full bg-slate-100 rounded overflow-hidden">
                    <div
                      className="h-full bg-[#0AA89C] transition-all"
                      style={{ width: `${videoProgress}%` }}
                    />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Enviando... {videoProgress}%</div>
                </div>
              )}

              {videoError && !videoUploading && (
                <div className="mb-3 p-2 rounded border border-red-200 bg-red-50 text-xs text-red-700" data-testid="video-error">
                  {videoError}
                </div>
              )}

              {!introVideo && !videoUploading && (
                <div
                  className="border border-dashed border-slate-300 rounded p-6 text-center text-sm text-slate-500 cursor-pointer hover:bg-slate-50 transition"
                  onClick={() => videoFileRef.current?.click()}
                  data-testid="video-empty-dropzone"
                >
                  <Film className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                  Nenhum vídeo cadastrado. Clique para enviar um arquivo MP4, WebM ou MOV.
                </div>
              )}

              {introVideo && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Pré-visualização</div>
                    <video
                      key={introVideo.path}
                      className="w-full rounded border border-slate-200 bg-slate-900 aspect-video object-cover"
                      src={fileUrl(introVideo.path)}
                      poster={introVideo.poster_path ? fileUrl(introVideo.poster_path) : undefined}
                      controls
                      muted
                      playsInline
                      preload="metadata"
                      data-testid="video-preview-player"
                    />
                  </div>
                  <div className="text-xs text-slate-600 space-y-2">
                    <div>
                      <span className="font-medium text-slate-500">Formato:</span> {introVideo.mime_type}
                    </div>
                    <div>
                      <span className="font-medium text-slate-500">Tamanho:</span> {(introVideo.size_bytes / (1024 * 1024)).toFixed(2)} MB
                    </div>
                    <div>
                      <span className="font-medium text-slate-500">Enviado em:</span>{" "}
                      {new Date(introVideo.uploaded_at).toLocaleString("pt-BR")}
                    </div>
                    {introVideo.uploaded_by && (
                      <div>
                        <span className="font-medium text-slate-500">Por:</span> {introVideo.uploaded_by}
                      </div>
                    )}
                    <div className="pt-2 border-t border-slate-100">
                      <Label className="text-xs">Imagem de capa (poster)</Label>
                      <p className="text-[11px] text-slate-500 mb-1">
                        Selecione uma foto do imóvel como capa do vídeo. Se nenhuma for escolhida, o site usará a foto principal automaticamente.
                      </p>
                      <select
                        className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 bg-white"
                        value={introVideo.poster_path || ""}
                        onChange={(e) => setVideoPoster(e.target.value || null)}
                        data-testid="video-poster-select"
                      >
                        <option value="">— Usar a foto principal —</option>
                        {photos.map((p) => (
                          <option key={p.id} value={p.storage_path}>
                            {p.is_cover ? "★ " : ""}{p.caption || p.original_filename || `Foto ${p.order + 1}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 border-t border-slate-200 pt-4">
              <ICalSyncManager houseId={house.id} houseName={house.name} />
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="bg-[#0AA89C] hover:bg-[#066B63]" onClick={submit} disabled={saving} data-testid="save-house">
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </DialogContent>

      {/* Caption / alt text editor */}
      <Dialog open={!!captionEditing} onOpenChange={(o) => !o && setCaptionEditing(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Legenda e texto alternativo</DialogTitle>
            <DialogDescription>
              A legenda aparece abaixo da foto no site. O texto alternativo (alt) descreve a imagem para leitores de tela e mecanismos de busca — ideal para acessibilidade e SEO.
            </DialogDescription>
          </DialogHeader>
          {captionEditing && (
            <div className="space-y-4">
              <img src={fileUrl(captionEditing.storage_path)} alt="" className="w-full aspect-[16/9] object-cover rounded border border-slate-200" />
              <div>
                <Label htmlFor="photo-caption">Legenda</Label>
                <Input id="photo-caption" value={captionEditing.caption || ""} onChange={(e) => setCaptionEditing({ ...captionEditing, caption: e.target.value })} data-testid="photo-caption-input" maxLength={140} />
                <div className="text-xs text-slate-400 mt-1">{(captionEditing.caption || "").length}/140</div>
              </div>
              <div>
                <Label htmlFor="photo-alt">Texto alternativo (alt)</Label>
                <Input id="photo-alt" value={captionEditing.alt_text || ""} onChange={(e) => setCaptionEditing({ ...captionEditing, alt_text: e.target.value })} data-testid="photo-alt-input" maxLength={140} placeholder="Ex: Varanda ampla com vista para o mar" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCaptionEditing(null)}>Cancelar</Button>
                <Button className="bg-[#0AA89C] hover:bg-[#066B63]" onClick={saveCaption} data-testid="save-photo-caption">Salvar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function SortablePhoto({ photo, houseId, onSetCover, onDelete, onReplaceFile, onEditCaption }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.id });
  const replaceRef = useRef();
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : "auto",
    opacity: isDragging ? 0.85 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group" data-testid={`photo-item-${photo.id}`}>
      <img
        src={fileUrl(photo.storage_path)}
        className="aspect-[4/3] w-full object-cover rounded border border-slate-200"
        alt={photo.alt_text || ""}
      />
      {photo.is_cover && <span className="absolute top-1 left-1 bg-[#3B9FD4] text-white text-[10px] px-1.5 py-0.5 rounded">Capa</span>}
      <button
        {...attributes}
        {...listeners}
        aria-label="Arrastar para reordenar"
        className="absolute top-1 right-1 bg-white/90 text-slate-600 rounded p-1 cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-[#0AA89C]/60"
        data-testid={`photo-drag-${photo.id}`}
      >
        <GripVertical className="w-3.5 h-3.5" />
      </button>
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 rounded flex items-center justify-center gap-2">
        {!photo.is_cover && (
          <button onClick={onSetCover} className="text-white p-1.5 rounded hover:bg-white/10" title="Definir como capa" data-testid={`photo-cover-${photo.id}`}>
            <Star className="w-4 h-4" />
          </button>
        )}
        <button onClick={onEditCaption} className="text-white p-1.5 rounded hover:bg-white/10" title="Editar legenda / alt" data-testid={`photo-edit-${photo.id}`}>
          <Pencil className="w-4 h-4" />
        </button>
        <input
          ref={replaceRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onReplaceFile(e.target.files[0])}
        />
        <button
          onClick={() => replaceRef.current?.click()}
          className="text-white p-1.5 rounded hover:bg-white/10"
          title="Substituir arquivo"
          data-testid={`photo-replace-${photo.id}`}
        >
          <RefreshCcw className="w-4 h-4" />
        </button>
        <button onClick={onDelete} className="text-white p-1.5 rounded hover:bg-white/10" title="Remover" data-testid={`photo-delete-${photo.id}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {photo.caption && (
        <div className="text-[11px] text-slate-500 mt-1 truncate" title={photo.caption}>
          {photo.caption}
        </div>
      )}
    </div>
  );
}

function NumField({ label, value, onChange, step = 1 }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type="number" step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}
